import { usePlayerDiagnostics } from "@components/SwipeList/Slot.diagnostics";
import { usePlayerStoreApi } from "@utils/player/core/usePlayer";
import LBox, { LBoxProps } from "@components/LBox";
import Progress from "@components/MusicPlayer/Progress";
import SwipeList from "@components/SwipeList/SwipeList";
import useBarMusicPlayer from "@hooks/useBarMusicPlayer";
import PlayerControls from "@components/BarMusicPlayer/PlayerControls";
import BarSwipeListItem, {
  BarSwipeListItemProvider,
} from "@components/BarMusicPlayer/BarSwipeListItem";
import type { LayoutChangeEvent } from "react-native";

import { selectCurrentItem } from "@utils/player/core/selectors";
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";
import { useEffect, useRef } from "react";
import { useActiveSongId } from "@utils/player";

const SPRING_CONFIG_HEIGHT = {
  mass: 0.3,
  damping: 15,
  stiffness: 400,
};

const SPRING_CONFIG_OPACITY = {
  mass: 0.2,
  damping: 12,
  stiffness: 300,
};

const BarMusicPlayer = ({
  progressStyle,
}: {
  progressStyle: LBoxProps["style"];
}) => {
  const { capturePlayerBlankRenderGate } = usePlayerDiagnostics();

  const playerStore = usePlayerStoreApi();

  const {
    itemHeight,
    itemWidth,
    openPlayer,
    setControlsWidth,
    BAR_MUSIC_PLAYER_HEIGHT,
  } = useBarMusicPlayer();

  // Use ref + shared value to avoid React re-renders on queue type changes
  const isForYouRef = useRef(
    playerStore.getState().activeQueueType === "foryou",
  );
  const isForYouActive = useSharedValue(
    playerStore.getState().activeQueueType === "foryou" ? 1 : 0,
  );
  const activeSongId = useActiveSongId();

  useEffect(() => {
    const unsubscribe = playerStore.subscribe((state) => {
      const newIsForYou = state.activeQueueType === "foryou";
      if (isForYouRef.current !== newIsForYou) {
        isForYouRef.current = newIsForYou;
        isForYouActive.value = newIsForYou ? 1 : 0;
      }
    });
    return unsubscribe;
  }, [isForYouActive]);

  // Use ref + shared value to avoid React re-renders on song changes
  const hasSongRef = useRef(!!selectCurrentItem(playerStore.getState()));
  const hasSong = useSharedValue(
    selectCurrentItem(playerStore.getState()) ? 1 : 0,
  );

  useEffect(() => {
    const unsubscribe = playerStore.subscribe((state) => {
      const nextHasSong = !!selectCurrentItem(state);
      if (hasSongRef.current !== nextHasSong) {
        hasSongRef.current = nextHasSong;
        hasSong.value = nextHasSong ? 1 : 0;
      }
    });
    return unsubscribe;
  }, [hasSong]);

  // Animated styles for SwipeList visibility - runs on UI thread
  const forYouSwipeStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    width: "100%",
    transform: [{ translateY: isForYouActive.value ? 0 : 1000 }],
    opacity: isForYouActive.value,
  }));

  const playlistSwipeStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    width: "100%",
    transform: [{ translateY: isForYouActive.value ? 1000 : 0 }],
    opacity: isForYouActive.value ? 0 : 1,
  }));

  const heightStyle = useAnimatedStyle(() => ({
    height: withSpring(
      hasSong.value ? BAR_MUSIC_PLAYER_HEIGHT : 0,
      SPRING_CONFIG_HEIGHT,
    ),
  }));

  const opacityStyle = useAnimatedStyle(() => ({
    opacity: withSpring(hasSong.value ? 1 : 0, SPRING_CONFIG_OPACITY),
  }));

  const forYouPointerProps = useAnimatedProps(() => ({
    pointerEvents: (isForYouActive.value ? "auto" : "none") as "auto" | "none",
  }));

  const playlistPointerProps = useAnimatedProps(() => ({
    pointerEvents: (isForYouActive.value ? "none" : "auto") as "auto" | "none",
  }));

  useEffect(() => {
    const timeout = setTimeout(() => {
      const state = playerStore.getState();
      const rawActiveQueueType = state.activeQueueType as
        string | null | undefined;
      const validQueueType =
        rawActiveQueueType === "foryou" || rawActiveQueueType === "playlist"
          ? rawActiveQueueType
          : null;
      const activeQueue =
        validQueueType === "playlist"
          ? state.playlistQueue
          : validQueueType === "foryou"
            ? state.foryouQueue
            : null;
      const currentItem =
        activeQueue && activeQueue.currentIndex >= 0
          ? activeQueue.items[activeQueue.currentIndex]
          : undefined;

      if (currentItem) return;

      const shouldHaveCurrentItem =
        (activeQueue?.items.length ?? 0) > 0 ||
        state.userIntent === "playing" ||
        state.status === "LOADING" ||
        state.status === "BUFFERING" ||
        state.status === "PLAYING";

      if (!shouldHaveCurrentItem) return;

      capturePlayerBlankRenderGate({
        extra: {
          activeQueueItemCount: activeQueue?.items.length ?? 0,
          activeQueueType: rawActiveQueueType ?? null,
          activeSongId,
          currentIndex: activeQueue?.currentIndex ?? null,
          hasSongSharedValue: Boolean(hasSong.get()),
          isForYouSharedValue: Boolean(isForYouActive.get()),
        },
        queueType: validQueueType ?? "none",
        reason: "bar-current-item-missing",
        surface: "bar",
      });
    }, 2_000);

    return () => clearTimeout(timeout);
  }, [activeSongId, hasSong, isForYouActive]);

  if (!activeSongId) return null;

  return (
    <LBox
      borderTopLeftRadius="l"
      borderTopRightRadius="l"
      gap="s3"
      width="100%"
      paddingHorizontal="s2"
      paddingTop="s2"
      style={heightStyle}
      overflow="hidden"
      bg="barMusicPlayerBg"
      py="s1"
    >
      <LBox
        flex={1}
        flexDirection="row"
        borderRadius="s"
        alignItems="center"
        mt="s2"
      >
        <LBox
          flex={1}
          height={BAR_MUSIC_PLAYER_HEIGHT}
          justifyContent="center"
          style={opacityStyle}
        >
          <>
            <LBox justifyContent="center" overflow="hidden" height={itemHeight}>
              <BarSwipeListItemProvider
                itemHeight={itemHeight}
                itemWidth={itemWidth}
                onPress={openPlayer}
              >
                {/* ForYou SwipeList - visibility controlled by UI thread */}
                <Animated.View
                  style={forYouSwipeStyle}
                  animatedProps={forYouPointerProps}
                >
                  <SwipeList
                    Component={BarSwipeListItem}
                    height={itemHeight}
                    queueType="foryou"
                    surface="bar"
                  />
                </Animated.View>
                {/* Playlist SwipeList - visibility controlled by UI thread */}
                <Animated.View
                  style={playlistSwipeStyle}
                  animatedProps={playlistPointerProps}
                >
                  <SwipeList
                    Component={BarSwipeListItem}
                    height={itemHeight}
                    queueType="playlist"
                    surface="bar"
                  />
                </Animated.View>
              </BarSwipeListItemProvider>
            </LBox>
          </>
        </LBox>
        <LBox
          onLayout={(e: LayoutChangeEvent) =>
            setControlsWidth(e.nativeEvent.layout.width)
          }
          style={opacityStyle}
        >
          <PlayerControls />
        </LBox>
      </LBox>
      <LBox style={[opacityStyle, progressStyle]}>
        <>
          <Progress />
        </>
      </LBox>
    </LBox>
  );
};

export default BarMusicPlayer;
